<template>
  <v-container>
    <div class="mt-2 mb-2">
      <span class="font-weight-bold" style="font-size: 16px;">{{ $t('graphics.dateRange') }}</span>
      <v-row class="align-center mt-2">
        <v-col
          lg="3"
          md="6"
          sm="12"
        >
          <v-menu
            ref="menu"
            v-model="menu"
            :closeOnContentClick="false"
            :returnValue.sync="dateRange"
            transition="scale-transition"
            offsetY
          >
            <template v-slot:activator="{ on, attrs }">
              <v-combobox
                v-model="dateRange"
                multiple
                chips
                smallChips
                :label="$t('graphics.dateRange')"
                prependIcon="mdi-calendar"
                readonly
                clearable
                v-bind="attrs"
                v-on="on"
                @click:clear="clearDates"
              />
            </template>
            <v-date-picker
              v-model="dateRange"
              range
              noTitle
              scrollable
              :allowedDates="allowedDates"
            >
              <v-spacer />
              <v-btn
                text
                color="primary"
                @click="menu = false"
              >
                {{ $t('graphics.labels.cancel') }}
              </v-btn>
              <v-btn
                text
                color="primary"
                @click="applyRange"
              >
                {{ $t('graphics.labels.ok') }}
              </v-btn>
            </v-date-picker>
          </v-menu>
        </v-col>

        <v-col
          lg="3"
          md="6"
          sm="12"
          class="d-flex align-center"
        >
          <span class="mr-2">{{ $t('labels.or') }}</span>
          <VSelect
            v-model="recurrency"
            outlined
            :label="$t('forms.labels.period')"
            dense
            hideDetails
            itemValue="key"
            :items="displayItems"
            data-cy="graphicPeriod"
            @change="onSettingChange('recurrency')"
          />
        </v-col>
      </v-row>
    </div>
  </v-container>
</template>

<script>
import VSelect from '@/components/shared/widgets/forms/VuetifyInputs/VSelect.vue';

export default {
    name: 'GraphicDateRange',
    components: {
        VSelect,
    },
    props: {
        graphicId: {
            type: String,
            default: null,
        },
        graphic: {
            type: Object,
            default: () => {},
        },
    },
    data() {
        return {
            dateRange: [],
            from: null,
            to: null,
            recurrency: null,
            menu: false,
        };
    },
    computed: {
        dateFrom() {
            const { dateFrom } = this.$store.getters.getGraphic(this.graphicId) || {};
            return dateFrom ? dateFrom.split('T')[0] : null;
        },
        dateTo() {
            const { dateTo } = this.$store.getters.getGraphic(this.graphicId) || {};
            return dateTo ? dateTo.split('T')[0] : null;
        },
        displayItems() {
            return ['lastWeek', 'lastMonth', 'lastThreeMonths', 'lastSixMonths'].map(time => ({
                name: this.$t(`graphics.${time}`),
                key: time,
            }));
        },
    },
    watch: {
        graphic: {
            handler() {
                this.recurrency = this.graphic.recurrency;
                this.updateDateRange();
            },
            deep: true,
        },
    },
    created() {
        this.updateDateRange();
        this.recurrency = this.graphic.recurrency;
    },
    methods: {
        updateDateRange() {
            this.from = this.dateFrom;
            this.to = this.dateTo;
            if (this.from && this.to) {
                this.dateRange = [this.from, this.to];
            } else {
                this.dateRange = [];
            }
        },
        updateGraphicDateRange(graphicId, graphic) {
            const { siteId } = this.$route.params;
            this.$store.dispatch('editGraphic', { siteId, graphicId, graphic });
        },

        convertToUtcMidnight(dateStr) {
            const [year, month, day] = dateStr.split('-').map(Number);
            return new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));
        },

        convertToUtcEndOfDay(dateStr) {
            const [year, month, day] = dateStr.split('-').map(Number);
            return new Date(Date.UTC(year, month - 1, day, 23, 59, 59, 999));
        },

        applyRange() {
            this.menu = false;
            if (!this.dateRange || this.dateRange.length < 2) return;

            let [start, end] = this.dateRange;
            if (new Date(start) > new Date(end)) {
                [start, end] = [end, start];
            }
            const payload = {
                dateFrom: this.convertToUtcMidnight(start),
                dateTo: this.convertToUtcEndOfDay(end),
            };

            if (this.recurrency) {
                payload.recurrency = null;
            }

            this.updateGraphicDateRange(this.graphicId, payload);
            this.$emit('dateRangeChange');
        },

        onSettingChange(key) {
            const payload = {
                [key]: this[key],
                dateFrom: null,
                dateTo: null,
            };
            this.dateRange = [];
            this.updateGraphicDateRange(this.graphicId, payload);
            this.$emit('dateRangeChange');
        },

        clearDates() {
            this.dateRange = [];
            const payload = { dateFrom: null, dateTo: null };
            if (this.recurrency) {
                payload.recurrency = null;
            }
            this.updateGraphicDateRange(this.graphicId, payload);
        },

        allowedDates(dateStr) {
            const [year, month, day] = dateStr.split('-').map(Number);
            const valDate = new Date(Date.UTC(year, month - 1, day, 0, 0, 0, 0));

            const nowLocal = new Date();
            const todayUTC = new Date(Date.UTC(
                nowLocal.getUTCFullYear(),
                nowLocal.getUTCMonth(),
                nowLocal.getUTCDate(),
                23, 59, 59, 999,
            ));

            if (valDate > todayUTC) {
                return false;
            }

            if (this.dateRange.length === 0) {
                return valDate <= todayUTC;
            }

            if (this.dateRange.length === 1) {
                const [startY, startM, startD] = this.dateRange[0].split('-').map(Number);
                const startDate = new Date(Date.UTC(startY, startM - 1, startD, 0, 0, 0, 0));

                const maxFromDate = new Date(Date.UTC(
                    startDate.getUTCFullYear() - 3,
                    startDate.getUTCMonth(),
                    startDate.getUTCDate(),
                    0, 0, 0, 0,
                ));

                const maxToDate = new Date(Date.UTC(
                    startDate.getUTCFullYear() + 3,
                    startDate.getUTCMonth(),
                    startDate.getUTCDate(),
                    23, 59, 59, 999,
                ));

                const upperBound = maxToDate < todayUTC ? maxToDate : todayUTC;

                return valDate >= maxFromDate && valDate <= upperBound;
            }

            return true;
        },
    },
};
</script>
